import type { ReactElement } from "react"
import { useCallback } from "react"
import ListMol from "@onestore/hel/dist/components/molecules/ListMol"
import BreakpointVisibilityOrg from "@onestore/hel/dist/components/organisms/BreakpointVisibilityOrg"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import FlexItemOrg from "@onestore/hel/dist/components/organisms/FlexItemOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import SurfaceOrg from "@onestore/hel/dist/components/organisms/SurfaceOrg"
import type {
  ButtonProps,
  Props as ModalTemplateProps,
} from "@onestore/hel/dist/components/templates/ModalTemplate"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import { animationDuration } from "@onestore/hel/dist/settings"
import _t from "@core/i18n"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type { HorizontalProductBox } from "@gatsby-plugin-generic-page/fragments/horizontalProductBox"
import { useConfigsHookContext } from "@gatsby-plugin-generic-page/sections/TableHorizontalBoxesSection"
import { Badge, Text } from "@ionos-web-design-system/react"
import ModalTemplateWrapper from "~/components/ModalTemplateWrapper"
import isEmpty from "~/lib/isEmpty"
import usePromoModalCells from "./usePromoModalCells"

type Props = {
  isOpen: boolean
  onClose: () => void
  buttonHref: string
  productData?: HorizontalProductBox
  promotedHorizontalProductBox?: HorizontalProductBox[]
}

const PromoModal = ({
  isOpen,
  onClose,
  buttonHref,
  productData,
  promotedHorizontalProductBox,
}: Props): ReactElement<Props> => {
  const { promoModalImage } = useConfigsHookContext() ?? {}
  const { productBoxName, cellsWithoutPrice } = usePromoModalCells(productData)
  const {
    productBoxName: promotedHorizontalProductBoxName,
    cellsWithoutPrice: promotedCellsWithoutPrice,
    buttonHref: promotedButtonHref,
    priceElement: promotedPriceElement,
  } = usePromoModalCells(promotedHorizontalProductBox?.[0])
  const isMediumUp = useBreakpoint("medium")

  const excludeTooltipTriggersFromModalAutofocus = useCallback(
    (node: HTMLDivElement | null) => {
      const tooltipTriggers =
        node?.querySelectorAll<HTMLElement>("button[data-state]")

      tooltipTriggers?.forEach((trigger) => {
        trigger.setAttribute("tabindex", "-1")
      })

      // ModalTemplate's useModalFocus autofocuses the second focusable
      // element `animationDuration.default` ms after the modal opens.
      // Hiding the tooltip trigger from that scan only until it has run
      // keeps the trigger reachable via Tab afterwards.
      setTimeout(() => {
        tooltipTriggers?.forEach((trigger) => {
          trigger.removeAttribute("tabindex")
        })
      }, animationDuration.default + 50)
    },
    []
  )

  const buttonLowerParams: ButtonProps = {
    title: _t("modal.vps.buttonLowerParams"),
    text: _t("modal.vps.buttonLowerParams"),
    href: buttonHref,
    variant: "text",
    color: "dark",
  }
  const buttonPromo: ButtonProps = {
    title: `${_t("marketplace.chooseButton")} ${promotedHorizontalProductBoxName}`,
    text: `${_t("marketplace.chooseButton")} ${promotedHorizontalProductBoxName}`,
    href: promotedButtonHref,
    color: "dark",
  }
  const buttons: ModalTemplateProps["buttons"] = isMediumUp
    ? [buttonLowerParams, buttonPromo]
    : [buttonPromo, buttonLowerParams]

  return (
    <ModalTemplateWrapper
      isOpen={isOpen}
      onCloseButtonClick={onClose}
      content={
        <>
          <FlexContainerOrg direction="column" gapSpace={3}>
            {!isEmpty(promoModalImage) ? (
              <BreakpointVisibilityOrg from="large">
                <Media media={promoModalImage} />
              </BreakpointVisibilityOrg>
            ) : null}

            <PushOrg bottomSpace={{ small: 1, medium: 1.5, large: 1.5 }}>
              <PushOrg bottomSpace={1}>
                <Text variant="heading2xl" weight="semibold" color="base">
                  {_t("modal.vps.title")}
                </Text>
              </PushOrg>

              <Text variant="body" color="base">
                {`${_t("modal.vps.description")} ${promotedHorizontalProductBoxName}`}
              </Text>
            </PushOrg>

            <FlexContainerOrg direction="row" gapSpace={2}>
              {isMediumUp ? (
                <FlexItemOrg flexGrow={1}>
                  <SurfaceOrg
                    spacingSpace={3}
                    color="backgroundLight"
                    hasBorder
                    borderWidth={1}
                    borderEmphasis="extraLow"
                    hasFullHeight
                  >
                    <FlexContainerOrg direction="column" gapSpace={2}>
                      <Text variant="body" weight="bold">
                        {productBoxName}
                      </Text>

                      <ListMol
                        items={cellsWithoutPrice.map((cell) => (
                          <FlexContainerOrg>
                            {cell.textWithoutStyles} {cell.title}
                          </FlexContainerOrg>
                        ))}
                        size="small"
                        type="dot"
                      />
                    </FlexContainerOrg>
                  </SurfaceOrg>
                </FlexItemOrg>
              ) : null}

              <FlexItemOrg flexGrow={1}>
                <SurfaceOrg
                  spacingSpace={isMediumUp ? 3 : 2}
                  color="backgroundLight"
                  hasBorder
                  borderWidth={2}
                  borderColor="primary"
                  labelOnTop={
                    <Badge variant="promo">
                      {_t("label.recommended").toUpperCase()}
                    </Badge>
                  }
                >
                  <FlexContainerOrg direction="column" gapSpace={2}>
                    <Text variant="body" weight="bold">
                      {promotedHorizontalProductBoxName}
                    </Text>

                    <ListMol
                      items={promotedCellsWithoutPrice.map((cell) => (
                        <FlexContainerOrg>
                          {cell.textWithoutStyles} {cell.title}
                        </FlexContainerOrg>
                      ))}
                      size="small"
                      type="tick"
                    />
                  </FlexContainerOrg>
                </SurfaceOrg>
              </FlexItemOrg>
            </FlexContainerOrg>
          </FlexContainerOrg>

          <PushOrg topSpace={2} bottomSpace={0.5} hasNoReset>
            <FlexContainerOrg
              ref={excludeTooltipTriggersFromModalAutofocus}
              direction="column"
              alignItems="flex-end"
            >
              {promotedPriceElement}
            </FlexContainerOrg>
          </PushOrg>
        </>
      }
      buttons={buttons}
      hasNoContentMargin
      hasAbsoluteCloseButton
      padding={{ mobile: 2, desktop: 3 }}
    />
  )
}

export default PromoModal
